// src/lib/environment.ts hardened/refactored v1
// Frontend-only utility for reading Amplify custom outputs.
// Runs in the browser — does NOT have access to process.env.
// For CDK synth-time environment detection, see amplify/backend.ts.

import outputs from '../../amplify_outputs.json';

type AmplifyEnv = 'dev' | 'test' | 'prod';

/**
 * Detect the current frontend environment by inspecting which
 * suffixed keys exist in amplify_outputs.json.
 *
 * NOTE: Detection is based on known stable output keys. If none
 * match, defaults to 'dev' — verify amplify_outputs.json is current
 * if behavior seems wrong.
 */
export function getAmplifyFrontendEnv(): AmplifyEnv {
  if (!outputs.custom) return 'dev';

  if ('PaymentAPIEndpoint-prod' in outputs.custom) return 'prod';
  if ('PaymentAPIEndpoint-test' in outputs.custom) return 'test';

  return 'dev';
}

/**
 * Get a custom Amplify output value by base name (without env suffix).
 * Automatically resolves the correct environment suffix.
 *
 * @param baseName - e.g. 'PaymentAPIEndpoint'
 * @returns The string value, or null if not found
 *
 * @example
 * const url = getAmplifyCustomOutput('PaymentAPIEndpoint');
 * if (!url) throw new Error('PaymentAPIEndpoint not configured');
 */
export function getAmplifyCustomOutput(baseName: string): string | null {
  if (!outputs.custom) return null;

  const env = getAmplifyFrontendEnv();
  const key = `${baseName}-${env}`;

  if (!(key in outputs.custom)) return null;

  const output = outputs.custom[key as keyof typeof outputs.custom];

  // Amplify custom outputs are wrapped as { Value: string } objects
  if (typeof output === 'object' && output !== null && 'Value' in output) {
    return typeof output.Value === 'string' ? output.Value : null;
  }

  return null;
}